import Client from './Client'

export interface IDatabase {
  id: string
  name: string
  encoding: string
  adminUser: number
  password: string
  schemas: string[] // todo add
}

export interface IDatabasePayload {
  id: string
  name: string
  encoding?: string
  adminUser?: number
  password?: string
  schemas?: string[]
}

export interface IDatabases {
  databases: IDatabase[]
}

export interface ISchema {
  db: string // todo add
  name: string
  collections: ICollectionMetadata[] // todo add
}

export interface ISchemaPayload {
  db: string // todo add
  name: string
  collections?: ICollectionMetadata[]
}

export interface ISchemas {
  schemas: string[]
}

export interface ISchemaCollections {
  tables: ICollection[]
}

export interface ICollectionMetadata {
  tableName: string
  isPublic: boolean
}

export interface ICollection {
  dbId: string // todo add
  schema: string // todo add
  name: string // todo add
  type: string
  selfReferencingColumnName: string
  referenceGeneration: string
  userDefinedTypeCatalog: string
  userDefinedTypeSchem: string
  userDefinedTypeName: string
  isInsertableInto: string
  isTyped: string
  commitAction: string
  columns: IColumn[]
  primaryKey: string[]
  indexes: IIndex[]
  foreignKeys: IForeignKey[]
}

export interface ICollectionPayload {
  dbId: string // todo add
  schema: string // todo add
  name: string // todo add
  type?: string
  selfReferencingColumnName?: string
  referenceGeneration?: string
  userDefinedTypeCatalog?: string
  userDefinedTypeSchem?: string
  userDefinedTypeName?: string
  isInsertableInto?: string
  isTyped?: string
  commitAction?: string
  columns?: IColumn[]
  primaryKey?: string[]
  indexes?: IIndex[]
  foreignKeys?: IForeignKey[]
}

export interface IColumn {
  columnName: string
  ordinalPosition: number
  columnDefault: string
  isNullable: string
  dataType: string
  characterMaximumLength: number
  characterOctetLength: number
  numericPrecision: number
  numericPrecisionRadix: number
  numericScale: number
  characterSetName: string
  collationName: string
}

export interface IDocument {
  id: string
  [fieldName: string]: any
}

interface IDocumentList {
  total: number
  items: IDocument[]
}

export interface IQuery {
  from?: number
  limit?: number
  calc_total?: number // 0 | 1
  filter?: string[] // fieldName:value
  order?: string[] // fieldName+ | fieldName-
}

export interface IIndex {
  name: string
  type: string
  column: string[]
}

export interface IForeignKey {
  name: string
  updateRule: string
  deleteRule: string
  referencesSchema: string
  referencesTable: string
  keyColumns: string[]
  referencesColumns: string[]
}

export interface IGetDatabaseLogResponse {
  scripts: IScript[]
}

export interface IScript {
  up: string
  down: string
  type: string
  status: string
  message: string
  time: string
}

export interface IExecuteDdlRequest {
  up: string
  down: string
}

const API_PREFIX = '/manager/api/v2/db/pg'

/**
 * Класс для работы с апи v1.0.1
 */
export default class PGManager {
  constructor(public readonly client: Client) {}
  /*
    Manager API
   */

  // Список баз данных
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  getDatabases(): Promise<IDatabases> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases`)
      .execute()
  }

  // Получить базу данных
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  getDatabase(dbName: string): Promise<IDatabase> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases/${dbName}`)
      .execute()
  }

  // Создать базу данных
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // table_exists
  createDatabase(payload: IDatabasePayload): Promise<IDatabase> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases`)
      .postJson(payload)
      .execute()
  }

  // Execute DDL
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // bad_request
  // db_not_found
  executeDDL(dbName: string, payload: IExecuteDdlRequest): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases/${dbName}`)
      .postJson(payload)
      .execute()
  }

  // Удалить базу данных
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // not_found
  dropDatabase(dbName: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases/${dbName}`)
      .delete()
      .execute()
  }

  // Logs
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // db_not_found
  getDatabaseLog(dbName: string): Promise<IGetDatabaseLogResponse> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/databases/${dbName}/log`)
      .execute()
  }

  // Schemas
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // db_not_found
  getSchemas(dbName: string): Promise<ISchemas> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/schemas/${dbName}`)
      .execute()
  }

  // Получить схему
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // db_not_found
  // schema_not_found
  getSchema(dbName: string, dbSchema: string): Promise<ISchema> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/schemas/${dbName}/${dbSchema}`)
      .execute()
  }

  // Создать коллекцию
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // schema_is_already_exists
  // db_not_exists
  createSchema(payload: ISchemaPayload): Promise<ISchema> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/schemas`)
      .postJson(payload)
      .execute()
  }

  // Удалить коллекцию
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // collection_not_empty
  // db_not_found
  // schema_not_found
  dropSchema(dbName: string, dbSchema: string): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/schemas/${dbName}/${dbSchema}`)
      .delete()
      .execute()
  }

  // Schemas
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // db_not_found
  // schema_not_found
  getCollections(
    dbName: string,
    dbSchema: string
  ): Promise<ISchemaCollections> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/collections/${dbName}/${dbSchema}`
      )
      .execute()
  }

  // Schemas
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // db_not_found
  // schema_not_found
  // collection_not_found
  getCollectionsInfo(
    dbName: string,
    dbSchema: string,
    dbCollection: string
  ): Promise<ICollection> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/collections/${dbName}/${dbSchema}/${dbCollection}`
      )
      .execute()
  }

  // Создать коллекцию
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // collection_exists
  // db_not_found
  // schema_not_found
  createCollection(payload: ICollectionPayload): Promise<ICollection> {
    return this.client.app
      .createAuthorizedRequest(`${API_PREFIX}/collections`)
      .postJson(payload)
      .execute()
  }

  // Удалить коллекцию
  // todo: реализовать метод на беке
  // -
  // Коды ошибок:
  // unauthorized
  // forbidden
  // operation_not_allowed
  // collection_not_empty
  // not_found
  dropCollection(
    dbName: string,
    dbSchema: string,
    dbCollection: string
  ): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/collections/${dbName}/${dbSchema}/${dbCollection}`
      )
      .delete()
      .execute()
  }

  /*
    Public API
   */
  getDocumentList(
    dbName: string,
    dbSchema: string,
    dbCollection: string,
    dbQueryParams: IQuery
  ): Promise<IDocumentList> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/documents/${dbName}/${dbSchema}/${dbCollection}`
      )
      .setQueryParams(dbQueryParams)
      .execute()
  }

  getDocumentById(
    dbName: string,
    dbSchema: string,
    dbCollection: string,
    id: string
  ): Promise<IDocument> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/documents/${dbName}/${dbSchema}/${dbCollection}/${id}`
      )
      .execute()
  }

  insertDocument(
    dbName: string,
    dbSchema: string,
    dbCollection: string,
    document: IDocument
  ): Promise<IDocument> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/documents/${dbName}/${dbSchema}/${dbCollection}`
      )
      .postJson(document)
      .execute()
  }

  updateDocument(
    dbName: string,
    dbSchema: string,
    dbCollection: string,
    id: string,
    document: IDocument
  ): Promise<IDocument> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/documents/${dbName}/${dbSchema}/${dbCollection}/${id}`
      )
      .putJson(document)
      .execute()
  }

  deleteDocument(
    dbName: string,
    dbSchema: string,
    dbCollection: string,
    id: string
  ): Promise<void> {
    return this.client.app
      .createAuthorizedRequest(
        `${API_PREFIX}/documents/${dbName}/${dbSchema}/${dbCollection}/${id}`
      )
      .delete()
      .execute()
  }
}
